import { GaiaAuthHttp } from '../../adapters/gaia/auth/GaiaAuthHttp';
import { GaiaExperienceDiscoveryHttp } from '../../adapters/gaia/experience/GaiaExperienceDiscoveryHttp';
import { GaiaExperienceWebSocket } from '../../adapters/gaia/experience/GaiaExperienceWebSocket';
import { generateDeviceId, loadOrCreateDeviceId } from '../../adapters/browser/storage/DeviceIdentity';
import { ExperienceSession, formatExchangeLog } from './ExperienceSession';
import { DEFAULT_GAIA_BASE_URL, createScriptedPorts, fakeModeEnabled, fakeNavigation } from './scriptedPorts';
import { resumeEnabled, speechRealEnabled, flagEnabled } from './pageFlags';
import type { AuthCredentials, AuthPort, AuthSession } from '../../ports/AuthPort';
import type { ExperienceSessionPorts } from './ExperienceSession';

/**
 * The pieces of the browser environment building a session needs. Kept as a
 * plain value instead of reading `import.meta.env`/`window` directly, so this
 * file stays framework-free and testable without a bundler.
 */
export interface SessionEnvironment {
  /** Gaia gateway base URL, already resolved from a build-time override or the default. */
  baseUrl: string;
  /** True in a dev build: turns on the window.__session/__authCalls test hooks. */
  dev: boolean;
  /** The page's query string, e.g. location.search. */
  search: string;
  /**
   * Keep the same device across reloads unless the address says "?resume=0", so a reload
   * resumes the player's run at its current step. The phone page sets this; the glasses
   * page and tests keep the default of a fresh device on every load.
   */
  resumeByDefault?: boolean;
}

/**
 * Builds the session a page run will drive.
 *
 * Against the real gateway the device signs itself in with a per-install id
 * and no password. In fake mode ("?fake=1") the ports answer on timers
 * instead, so the sting, the first line and a talk turn can all be seen
 * without a server. In a dev build the session and its login count are
 * exposed on `window.__session`/`window.__authCalls` for the e2e suites.
 */
export function buildSession(env: SessionEnvironment): ExperienceSession {
  return buildSessionWithPorts(env).session;
}

/**
 * Same as `buildSession`, but also returns the ports and device id it built the
 * session with, so tests can assert which concrete classes were wired in.
 *
 * Under "?fake=1&speech=real" the discovery and experience ports stay
 * scripted (nothing there validates the token, so any bearer string works),
 * but sign-in is real: a real per-install device id signs in against the
 * gateway's auth port, so the token handed to the real speech routes is a
 * real bearer token instead of the scripted auth port's pretend one.
 */
export function buildSessionWithPorts(
  env: SessionEnvironment,
): { session: ExperienceSession; ports: ExperienceSessionPorts; deviceId: string } {
  const fake = fakeModeEnabled(env.search);
  const realAuthUnderFake = fake && speechRealEnabled(env.search);
  const deviceId =
    fake && !realAuthUnderFake
      ? 'fake-device'
      : resumeEnabled(env.search) || (env.resumeByDefault && new URLSearchParams(env.search).get('resume') !== '0')
        ? loadOrCreateDeviceId()
        : generateDeviceId();
  const credentials: AuthCredentials = {
    kind: 'device',
    deviceId,
    platform: 'rayban-display',
  };
  // "?fake=1&nav=1" additionally scripts a display.map presentation
  // directive on a line, so the compass/map cue can be demoed with no
  // server (e.g. in Meta's simulator). "?fake=1&cues=1" scripts a line
  // with a display.text cue and a display.choice.
  const scriptedOptions = fake
    ? {
        ...(flagEnabled(env.search, 'nav') ? { navigation: fakeNavigation() } : {}),
        ...(flagEnabled(env.search, 'cues') ? { cues: true } : {}),
      }
    : {};
  const ports = realAuthUnderFake
    ? { ...createScriptedPorts(scriptedOptions), auth: new GaiaAuthHttp(env.baseUrl) }
    : fake
      ? createScriptedPorts(scriptedOptions)
      : realPorts(env.baseUrl, new URLSearchParams(env.search).get('run') ?? undefined);
  const session = new ExperienceSession(
    { ...ports, auth: countLogins(ports.auth, env.dev) },
    credentials,
  );

  if (env.dev) {
    window.__session = session;
  }
  // Always exposed, not only in dev builds: it holds nothing but the
  // conversation text and testers need it against the deployed build.
  window.__exchangeLog = () => formatExchangeLog(session.exchangeLog);
  return { session, ports, deviceId };
}

/** Opening the page with "?session=off" leaves the session idle. */
export function sessionEnabled(search: string): boolean {
  return new URLSearchParams(search).get('session') !== 'off';
}

function realPorts(baseUrl: string, requestedSessionId?: string): ExperienceSessionPorts {
  return {
    auth: new GaiaAuthHttp(baseUrl),
    discovery: new GaiaExperienceDiscoveryHttp(baseUrl),
    experience: new GaiaExperienceWebSocket(baseUrl, {requestedSessionId, locate: currentPosition}),
  };
}

/** One position fix for connect, or null after a few seconds; never blocks the session. */
function currentPosition(): Promise<{ latitude: number; longitude: number } | null> {
  const geolocation = globalThis.navigator?.geolocation;
  if (!geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    geolocation.getCurrentPosition(
      (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 5_000 }
    );
  });
}

/** Counts sign-ins on window.__authCalls, which the end to end tests read. */
function countLogins(auth: AuthPort, dev: boolean): AuthPort {
  if (!dev) return auth;
  window.__authCalls = 0;
  return {
    login: (credentials: AuthCredentials): Promise<AuthSession> => {
      window.__authCalls = (window.__authCalls ?? 0) + 1;
      return auth.login(credentials);
    },
    refresh: (refreshToken: string, accessToken?: string) => auth.refresh(refreshToken, accessToken),
    validate: (accessToken: string) => auth.validate(accessToken),
    logout: (accessToken: string) => auth.logout(accessToken),
  };
}

declare global {
  interface Window {
    __session?: ExperienceSession;
    __authCalls?: number;
    __exchangeLog?: () => string;
  }
}
